import { useMemo, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierPickupSlot,
  type CarrierPickupSlotInput,
  useCreateCarrierPickupSlot,
  useDeleteCarrierPickupSlotCapacityOverride,
  useUpdateCarrierPickupSlot,
  useUpsertCarrierPickupSlotCapacityOverride,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { hasHttpStatus } from "@/lib/api-error";
import {
  ALL_PICKUP_SERVICE_DAYS,
  PICKUP_SERVICE_DAY_OPTIONS,
  sortPickupServiceDays,
} from "@/features/pickup-service-days";

type DispatchOption = { id: string; code: string; name: string };

export function PickupSlotDialog({
  serviceId,
  dispatches,
  slot,
  onClose,
}: {
  serviceId: string;
  dispatches: DispatchOption[];
  slot?: CarrierPickupSlot;
  onClose: () => void;
}) {
  const create = useCreateCarrierPickupSlot(serviceId);
  const update = useUpdateCarrierPickupSlot();
  const upsertOverride = useUpsertCarrierPickupSlotCapacityOverride();
  const deleteOverride = useDeleteCarrierPickupSlotCapacityOverride();
  const initialForm = useMemo<CarrierPickupSlotInput>(
    () => ({
      dispatchCenterId: slot?.dispatchCenterId ?? dispatches[0]?.id ?? "",
      name: slot?.name ?? "",
      startTime: slot?.startTime ?? "09:00",
      endTime: slot?.endTime ?? "12:00",
      serviceDays: slot?.serviceDays ?? [...ALL_PICKUP_SERVICE_DAYS],
      bookingLeadMinutes: slot?.bookingLeadMinutes ?? 60,
      defaultCapacity: slot?.defaultCapacity ?? null,
      isActive: slot?.isActive ?? true,
      sortOrder: slot?.sortOrder ?? 0,
    }),
    [dispatches, slot]
  );
  const [form, setForm] = useState<CarrierPickupSlotInput>(initialForm);
  const [overrideDate, setOverrideDate] = useState("");
  const [overrideCapacity, setOverrideCapacity] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submissionLock = useRef(false);
  const pending =
    submitting ||
    create.isPending ||
    update.isPending ||
    upsertOverride.isPending ||
    deleteOverride.isPending;
  const slotFormDirty = JSON.stringify(form) !== JSON.stringify(initialForm);
  const overrideDraftDirty = Boolean(overrideDate || overrideCapacity);
  const dirty = slotFormDirty || overrideDraftDirty;

  const close = () => {
    if (pending) return;
    if (dirty && !window.confirm("Discard unsaved pickup slot changes?")) {
      return;
    }
    onClose();
  };

  const saveSlot = async () => {
    if (submissionLock.current) return;
    setError(null);
    if (
      !form.dispatchCenterId ||
      !form.name.trim() ||
      !form.startTime ||
      !form.endTime ||
      form.startTime >= form.endTime
    ) {
      setError("Choose a dispatch, name, and valid time window.");
      return;
    }
    if (form.serviceDays.length === 0) {
      setError("Choose at least one pickup service day.");
      return;
    }
    if (
      !Number.isInteger(form.bookingLeadMinutes) ||
      Number(form.bookingLeadMinutes) < 0 ||
      (form.defaultCapacity != null &&
        (!Number.isInteger(form.defaultCapacity) ||
          form.defaultCapacity < 0)) ||
      !Number.isInteger(form.sortOrder)
    ) {
      setError(
        "Lead time, capacity, and sort order must be whole non-negative numbers."
      );
      return;
    }

    submissionLock.current = true;
    setSubmitting(true);
    try {
      if (slot) {
        await update.mutateAsync({ slotId: slot.id, serviceId, patch: form });
        toast.success("Pickup slot updated.");
      } else {
        await create.mutateAsync(form);
        toast.success("Pickup slot created.");
      }
      onClose();
    } catch (caught) {
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to save pickup slots."
        : errorMessage(caught, "Pickup slot could not be saved.");
      setError(message);
      toast.error(message);
    } finally {
      submissionLock.current = false;
      setSubmitting(false);
    }
  };

  const addOverride = async () => {
    if (submissionLock.current) return;
    setError(null);
    if (slotFormDirty) {
      setError("Save the pickup slot before changing date overrides.");
      return;
    }
    const maxCapacity = Number(overrideCapacity);
    if (
      !slot ||
      !/^\d{4}-\d{2}-\d{2}$/.test(overrideDate) ||
      !Number.isInteger(maxCapacity) ||
      maxCapacity < 0
    ) {
      setError("Choose a date and a non-negative capacity.");
      return;
    }

    submissionLock.current = true;
    setSubmitting(true);
    try {
      await upsertOverride.mutateAsync({
        serviceId,
        slotId: slot.id,
        date: overrideDate,
        maxCapacity,
      });
      setOverrideDate("");
      setOverrideCapacity("");
      toast.success("Capacity override saved.");
      onClose();
    } catch (caught) {
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to update capacity overrides."
        : errorMessage(caught, "Capacity override could not be saved.");
      setError(message);
      toast.error(message);
    } finally {
      submissionLock.current = false;
      setSubmitting(false);
    }
  };

  const removeOverride = async (overrideId: string) => {
    setError(null);
    if (slotFormDirty) {
      setError("Save the pickup slot before changing date overrides.");
      return;
    }
    try {
      await deleteOverride.mutateAsync({ serviceId, overrideId });
      toast.success("Capacity override deleted.");
      onClose();
    } catch (caught) {
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to update capacity overrides."
        : errorMessage(caught, "Capacity override could not be deleted.");
      setError(message);
      toast.error(message);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title={slot ? `Edit ${slot.name}` : "New pickup slot"}
      presentation="responsive-sheet"
      className="md:max-w-2xl"
      closeDisabled={pending}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {dirty ? "Unsaved changes" : "No unsaved changes"}
          </span>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              variant="outline"
              size="touch"
              className="w-full sm:w-auto"
              onClick={close}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              size="touch"
              className="w-full sm:w-auto"
              onClick={() => void saveSlot()}
              disabled={pending || !slotFormDirty}
            >
              {pending ? "Saving…" : "Save slot"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label
            htmlFor="pickup-slot-name"
            className="space-y-1 text-sm font-medium sm:col-span-2"
          >
            <span>Name</span>
            <Input
              id="pickup-slot-name"
              aria-label="Slot name"
              value={form.name}
              onChange={(event) =>
                setForm({ ...form, name: event.target.value })
              }
              placeholder="Morning"
              disabled={pending}
            />
          </label>
          <label
            htmlFor="pickup-slot-dispatch"
            className="space-y-1 text-sm font-medium sm:col-span-2"
          >
            <span>Dispatch center</span>
            <Select
              value={form.dispatchCenterId}
              onValueChange={(dispatchCenterId) =>
                setForm({ ...form, dispatchCenterId })
              }
            >
              <SelectTrigger
                id="pickup-slot-dispatch"
                aria-label="Slot dispatch center"
                size="sm"
                className="w-full"
                disabled={pending}
              >
                <SelectValue placeholder="Select dispatch center" />
              </SelectTrigger>
              <SelectContent>
                {dispatches.map((dispatch) => (
                  <SelectItem key={dispatch.id} value={dispatch.id}>
                    {dispatch.name} ({dispatch.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label
            htmlFor="pickup-slot-start"
            className="space-y-1 text-sm font-medium"
          >
            <span>Start time</span>
            <Input
              id="pickup-slot-start"
              type="time"
              aria-label="Start time"
              value={form.startTime}
              onChange={(event) =>
                setForm({ ...form, startTime: event.target.value })
              }
              disabled={pending}
            />
          </label>
          <label
            htmlFor="pickup-slot-end"
            className="space-y-1 text-sm font-medium"
          >
            <span>End time</span>
            <Input
              id="pickup-slot-end"
              type="time"
              aria-label="End time"
              value={form.endTime}
              onChange={(event) =>
                setForm({ ...form, endTime: event.target.value })
              }
              disabled={pending}
            />
          </label>
          <fieldset className="space-y-2 sm:col-span-2">
            <legend className="text-sm font-medium">Pickup service days</legend>
            <p className="text-xs text-muted-foreground">
              Customers can book this pickup window only on selected days.
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {PICKUP_SERVICE_DAY_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  htmlFor={`pickup-service-day-${option.value}`}
                  className="flex min-h-11 items-center gap-2 rounded-md border px-3 text-sm"
                >
                  <Checkbox
                    id={`pickup-service-day-${option.value}`}
                    checked={form.serviceDays.includes(option.value)}
                    onCheckedChange={(checked) => {
                      const serviceDays = checked
                        ? [...form.serviceDays, option.value]
                        : form.serviceDays.filter(
                            (day) => day !== option.value
                          );
                      setForm({
                        ...form,
                        serviceDays: sortPickupServiceDays(serviceDays),
                      });
                    }}
                    disabled={pending}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </fieldset>
          <label
            htmlFor="pickup-slot-lead"
            className="space-y-1 text-sm font-medium"
          >
            <span>Booking lead (minutes)</span>
            <Input
              id="pickup-slot-lead"
              type="number"
              min={0}
              aria-label="Booking lead minutes"
              value={form.bookingLeadMinutes ?? 60}
              onChange={(event) =>
                setForm({
                  ...form,
                  bookingLeadMinutes: Number(event.target.value),
                })
              }
              disabled={pending}
            />
          </label>
          <label
            htmlFor="pickup-slot-capacity"
            className="space-y-1 text-sm font-medium"
          >
            <span>Default capacity</span>
            <Input
              id="pickup-slot-capacity"
              type="number"
              min={0}
              aria-label="Default capacity"
              placeholder="Unlimited"
              value={form.defaultCapacity ?? ""}
              onChange={(event) =>
                setForm({
                  ...form,
                  defaultCapacity: event.target.value
                    ? Number(event.target.value)
                    : null,
                })
              }
              disabled={pending}
            />
          </label>
          <label
            htmlFor="pickup-slot-sort-order"
            className="space-y-1 text-sm font-medium"
          >
            <span>Sort order</span>
            <Input
              id="pickup-slot-sort-order"
              type="number"
              min={0}
              aria-label="Sort order"
              value={form.sortOrder ?? 0}
              onChange={(event) =>
                setForm({ ...form, sortOrder: Number(event.target.value) })
              }
              disabled={pending}
            />
          </label>
        </div>
        <label
          htmlFor="pickup-slot-active"
          className="flex min-h-11 items-center gap-3 text-sm font-medium"
        >
          <Checkbox
            id="pickup-slot-active"
            checked={form.isActive ?? true}
            onCheckedChange={(checked) =>
              setForm({ ...form, isActive: checked === true })
            }
            disabled={pending}
          />
          Active for booking
        </label>

        {slot && (
          <div className="space-y-3 rounded-md border p-3">
            <div>
              <p className="font-medium">Date capacity overrides</p>
              <p className="text-xs text-muted-foreground">
                Set capacity to 0 to close this slot for one date.
              </p>
            </div>
            {(slot.capacityOverrides ?? []).map((override) => (
              <div
                key={override.id}
                className="flex items-center justify-between rounded bg-muted/40 px-3 py-2 text-sm"
              >
                <span>
                  {override.date.slice(0, 10)} — {override.maxCapacity}
                </span>
                <Button
                  variant="ghost"
                  size="icon-xs-touch"
                  aria-label={`Delete ${override.date.slice(0, 10)} override`}
                  disabled={pending}
                  onClick={() => void removeOverride(override.id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
            <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <label
                htmlFor="pickup-slot-override-date"
                className="space-y-1 text-xs font-medium"
              >
                <span>Date</span>
                <Input
                  id="pickup-slot-override-date"
                  type="date"
                  aria-label="Override date"
                  value={overrideDate}
                  onChange={(event) => setOverrideDate(event.target.value)}
                  disabled={pending}
                />
              </label>
              <label
                htmlFor="pickup-slot-override-capacity"
                className="space-y-1 text-xs font-medium"
              >
                <span>Capacity</span>
                <Input
                  id="pickup-slot-override-capacity"
                  type="number"
                  min={0}
                  aria-label="Override capacity"
                  value={overrideCapacity}
                  onChange={(event) => setOverrideCapacity(event.target.value)}
                  disabled={pending}
                />
              </label>
              <Button
                variant="outline"
                size="touch"
                className="w-full"
                onClick={() => void addOverride()}
                disabled={pending}
              >
                Add
              </Button>
            </div>
          </div>
        )}

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function errorMessage(error: unknown, fallback: string) {
  const response = error as { response?: { data?: { error?: string } } };
  return response.response?.data?.error ?? fallback;
}
